import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserPermissions } from 'src/app/@core/enums/permissions.enum';
import { UserPermission } from 'src/app/@core/enums/user-permission.enum';
import { IdentityModel } from 'src/app/@core/models/identity.model';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { AuthService } from 'src/app/auth/auth.service';
import { NotificationResponseModel } from 'src/app/notification/model/notification.model';
import { NotificationHubService } from 'src/app/notification/service/notification-hub.service';
import { CartModel } from 'src/app/payment/models/cart.model';
import { PaymentService } from 'src/app/payment/services/payment.service';
import { LanguageService } from 'src/app/services/language.service';
import { UserModel } from 'src/app/student/models/user.model';
import { UserService } from 'src/app/student/services/user.service';
import { environment } from 'src/environments/environment';
import { UserNotificationService } from '../../../notification/service/user-notification.service';
import { LanguageEnum } from '../../models/item.model';
import { DigitalLibraryService } from 'src/app/digital-library/services/digital-library.service';
import { FilterModel } from 'src/app/@core/models/filter.model';
import { ResponseResult } from 'src/app/@core/models/response.model';
import { CategoryContentsModel } from 'src/app/digital-library/models/category-contents.model';
import { buildHeaderLinks, PortalLink, PortalLinkContext } from 'src/app/@core/navigation/portal-links';
import { PortalNavigationService } from 'src/app/@core/navigation/portal-navigation.service';

@Component({
  selector: 'app-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss'],
})
export class MainNavComponent implements OnInit {
  @Input() isContent: boolean;
  @Input() title: string;
  @Input() courseLink: string;
  @Input() hideSearch: boolean = false;
  @Output() showSearch: EventEmitter<boolean> = new EventEmitter(false);
  @ViewChild('nav') nav: ElementRef;
  private destroySubject: Subject<void> = new Subject();
  notificationsList: Array<NotificationResponseModel> = [];
  identityModel: IdentityModel = {} as IdentityModel;
  user: UserModel = {} as UserModel;
  showProcedures: boolean = environment.mohFeatures;
  mediaUrl: string = environment.mediaUrl;
  dashboardUrl = environment.dashboardUrl;
  toggleNavNotification: boolean = false;
  hideMenuColor: boolean = false;
  isCourses: boolean = false;
  isAuthorize: boolean;
  isFixed: boolean;
  isMenuCollapsed = true;
  date: string = '';
  setActive: any;
  searchInput: any;
  isRtl = true;
  lang: any;
  isDiploma: boolean;
  isLearningPath: boolean;
  showDigitalLibrary: boolean;
  isDark: boolean;

  // Unified Code mode (criteria 29 / 44): DGA nav header and off-canvas nav drawer.
  @ViewChild('drawerToggle') drawerToggle: ElementRef<HTMLButtonElement>;
  @ViewChild('drawerClose') drawerClose: ElementRef<HTMLButtonElement>;
  @ViewChild('drawerPanel') drawerPanel: ElementRef<HTMLElement>;
  unified = false;
  headerLinks: PortalLink[] = [];
  drawerOpen = false;
  isScrolled = false;
  private readonly drawerBreakpoint = 992;

  constructor(
    private langService: LanguageService,
    private router: Router,
    private authService: AuthService,
    private identityService: IdentityService,
    private notificationHubService: NotificationHubService,
    private userNotificationService: UserNotificationService,
    public paymentService: PaymentService,
    private digitalLibraryService: DigitalLibraryService,
    private portalNavigationService: PortalNavigationService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.getIdentity();
    this.unified = this.identityService.isUnifiedCode();
    this.isRtl = this.langService.getCurrentLanguage() === LanguageEnum.ar;
    this.langService.languageChange
      .pipe(takeUntil(this.destroySubject))
      .subscribe(
        (result: LanguageEnum) => (this.isRtl = result === LanguageEnum.ar)
      );
  }

  ngOnInit(): void {
    this.notificationHubService.onNotificationRecived
      .pipe(takeUntil(this.destroySubject))
      .subscribe((data) => {
        this.notificationsList.push({
          id: data.id,
          title: data.title,
          messageAr: data.message,
          englishMessage: data.message,
          createdDate: new Date(),
        } as any);
      });

    this.router.events
      .pipe(takeUntil(this.destroySubject))
      .subscribe((event: Event) => {
        if (event instanceof NavigationEnd) {
          if (this.drawerOpen) this.closeDrawer(false);
          this.hideMenuColor = event.url === '/' || event.url === '/home';
          this.isCourses =
            event.url === '/course' ||
            event.url === '/digital-library/contents';
        }
      });

    if (this.authService.isAuthenticated) {
      this.authService.currentUser
        .pipe(takeUntil(this.destroySubject))
        .subscribe((user: UserModel) => {
          this.isAuthorize = user?.email ? true : false;
          this.fetchUserNotifications();
          this.user = user;
          this.getUserCart();
        });
    }

    if (this.unified) {
      // The navigation service shares the digital-library check and the pages with the footer and sitemap.
      this.portalNavigationService.context$
        .pipe(takeUntil(this.destroySubject))
        .subscribe((context: PortalLinkContext) => {
          this.showDigitalLibrary = context.showDigitalLibrary;
          this.headerLinks = buildHeaderLinks(context);
        });
    } else {
      this.getLibrary();
    }
  }

  getLibrary(): void {
    this.digitalLibraryService.getPaging(new FilterModel(0, 5)).subscribe({
      next: (result: ResponseResult<Array<CategoryContentsModel>>) =>
        (this.showDigitalLibrary = result.total !== 0),
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.unified) {
      this.isScrolled = document.body.scrollTop > 20 || document.documentElement.scrollTop > 20;
      return;
    }
    if (
      document.body.scrollTop > 20 ||
      document.documentElement.scrollTop > 20
    ) {
      document.getElementsByTagName('nav').item(0).classList.add('shadow-sm');
    } else {
      document
        .getElementsByTagName('nav')
        .item(0)
        .classList.remove('shadow-sm');
    }
  }

  fetchUserNotifications(): void {
    if (!this.isAuthorize) return;

    // this.userNotificationService
    //   .getList()
    //   .pipe(takeUntil(this.destroySubject))
    //   .subscribe((data) => {
    //     if (Array.isArray(data.data)) {
    //       this.notificationsList = data.data;
    //     }
    //   });
  }

  get roles() {
    return UserPermission;
  }

  get permissions() {
    return UserPermissions;
  }

  public changeLanguageParent(): void {
    this.langService.toggle();
    this.lang = !this.lang;
  }

  onSearch(): void {
    this.hideSearch = !this.hideSearch;
    this.showSearch.emit(this.hideSearch);
  }

  signOut() {
    this.authService.logout();
  }

  login() {
    this.router.navigateByUrl('join/login');
  }

  ssoLogin() {
    this.authService
      .SSOLogin()
      .pipe(takeUntil(this.destroySubject))
      .subscribe((result: any) => {
        window.location = result.url;
      });
  }

  getIdentity() {
    this.identityModel = this.identityService.getIdentityModel();
  }

  toggleNavNotify(): void {
    this.toggleNavNotification = !this.toggleNavNotification;
  }

  seenNotification(notification: NotificationResponseModel): void {
    if (notification) {
      this.userNotificationService
        .updateSeenStatus(notification.id)
        .pipe(takeUntil(this.destroySubject))
        .subscribe((_) => {
          this.fetchUserNotifications();
          if (notification.path) this.router.navigate([notification.path]);
        });
    }
  }

  getUserCart() {
    var cartItems = JSON.parse(localStorage.getItem('cartItems')) ?? [];

    if (this.isAuthorize && cartItems.length > 0) {
      localStorage.removeItem('cartItems');
      var courseIds: any[] = [];
      cartItems.map((c) => {
        courseIds.push(c.courseId);
      });
      var body = { courseIds: courseIds };
      this.paymentService.updateCart(body).subscribe(() => {
        this.paymentService.getCart().subscribe((result: CartModel[]) => {
          this.paymentService.itemsInCart = result.length;
        });
      });
    } else if (this.isAuthorize) {
      this.paymentService.getCart().subscribe((result: CartModel[]) => {
        this.paymentService.itemsInCart = result.length;
      });
    } else {
      this.paymentService.itemsInCart = cartItems.length;
    }
  }

  otherActive() {
    this.langService.togglebg(false);
  }

  changeFontSize(event: any): void {
    document.body.style.setProperty('--font-size', event.target.value);
  }

  changeTheme(): void {
    this.isDark = !this.isDark;
    const colors = new Map([
      ['bg', this.isDark ? '#0E0E0E' : '#FFF'],
      ['text', this.isDark ? '#E7E7E7' : '#333'],
      ['black', this.isDark ? '#E7E7E7' : '#000'],
      ['white', this.isDark ? '#1E1E1E' : '#FFF'],
      ['gray', this.isDark ? '#1E1E1E' : '#F1F2F3'],
      ['light-gray', this.isDark ? '#1E1E1E' : '#FCFCFC'],
      ['border', this.isDark ? '#263238' : '#E0E0E0'],
    ]);
    Array.from(colors.entries()).forEach(([name, value]) =>
      document.body.style.setProperty(`--identity-${name}`, value)
    );
  }

  get organizationName(): string {
    const model = this.identityModel ?? ({} as IdentityModel);
    return (this.isRtl ? model.organizationNameAr || model.organizationName : model.organizationName || model.organizationNameAr) || '';
  }

  linkText(link: PortalLink): string {
    return (this.isRtl ? link.titleAr || link.title : link.title || link.titleAr) ?? '';
  }

  trackByLinkId(_: number, link: PortalLink): string {
    return link.id;
  }

  onPrimaryLinkClick(link: PortalLink, variant: string): void {
    if (link.url) this.otherActive();
    // Also covers a link to the current page, which triggers no NavigationEnd.
    if (variant === 'drawer') this.closeDrawer(false);
  }

  openDrawer(): void {
    if (this.drawerOpen) return;
    this.drawerOpen = true;
    document.body.classList.add('uc-scroll-locked');
    // Render now so the dialog is un-hidden and the view queries point at it before focus moves in.
    this.changeDetector.detectChanges();
    this.focusIntoDrawer();
  }

  closeDrawer(returnFocus: boolean = true): void {
    if (!this.drawerOpen) return;
    this.drawerOpen = false;
    document.body.classList.remove('uc-scroll-locked');
    this.changeDetector.detectChanges();
    if (returnFocus) this.drawerToggle?.nativeElement.focus({ preventScroll: true });
  }

  /**
   * Moves focus to the close button (or the first focusable element, or the dialog itself) once it is rendered and
   * visible; retries shortly after if the dialog is not laid out yet.
   */
  private focusIntoDrawer(attempt: number = 0): void {
    if (!this.drawerOpen) return;
    const panel = this.drawerPanel?.nativeElement;
    const target = [this.drawerClose?.nativeElement, ...this.drawerFocusables(), panel].find(
      (element) => !!element && this.isRendered(element)
    );
    target?.focus({ preventScroll: true });
    if (panel?.contains(document.activeElement)) return;
    // Timers rather than requestAnimationFrame: rAF is paused in hidden/background tabs.
    if (attempt < 10) setTimeout(() => this.focusIntoDrawer(attempt + 1), 16);
  }

  private drawerFocusables(): HTMLElement[] {
    const panel = this.drawerPanel?.nativeElement;
    if (!panel) return [];
    return Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => this.isRendered(element));
  }

  private isRendered(element: HTMLElement): boolean {
    return element.getClientRects().length > 0;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.drawerOpen) this.closeDrawer();
  }

  /** Modal behaviour: focus that lands outside the open dialog (e.g. back on the toggle) is pulled back into it. */
  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent): void {
    const panel = this.drawerPanel?.nativeElement;
    if (!this.drawerOpen || !panel || panel.contains(event.target as Node)) return;
    // Deferred: moving focus while the browser is still dispatching a focus event is not reliable.
    setTimeout(() => this.focusIntoDrawer());
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.drawerOpen && window.innerWidth >= this.drawerBreakpoint) this.closeDrawer(false);
  }

  /** Keeps Tab / Shift+Tab inside the open drawer (modal dialog). */
  onDrawerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const focusable = this.drawerFocusables();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === this.drawerPanel?.nativeElement)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('uc-scroll-locked');
    this.destroySubject.next();
  }
}
